import React, { useState } from 'react';
import AIEditor from './AIEditor';
import { useAIQuery } from 'presentation/components/KAI/KAI.hook';
import { AIQueryTypes, AIProviders } from '@hone-automation/common';
import { parseAIResponse } from 'presentation/utils/aiJSONParser';
import { ReportViewModel } from 'types/report';

interface AIPromptEditorProps {
  initialPrompt?: string;
  variableData?: Record<string, any>;
  onClose?: () => void;
  onResponse?: (response: any, error: any) => void;
  additionalTabs?: Array<{ id: string; label: string; callback?: () => void }>;
  activeTabId?: string;
  onTabChange?: (tabId: string) => void;
  parseResponse?: boolean;
  queryType?: any; // Using any to match your AIQueryTypes
  reportData?: ReportViewModel | null; // Add reportData prop
  onConstrainChange?: (isConstrained: boolean) => void; // Add constrain callback
}

const AIPromptEditor: React.FC<AIPromptEditorProps> = ({
  initialPrompt = '',
  variableData = {},
  onClose,
  onResponse,
  additionalTabs = [],
  activeTabId = 'preview',
  onTabChange,
  parseResponse = false,
  queryType = AIQueryTypes.Report_Analysis,
  reportData = null,
  onConstrainChange,
}) => {
  const { sendQuery } = useAIQuery();
  const [responseData, setResponseData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Handle running the prompt
  const handleRun = async (resolvedPrompt: string, variables: Record<string, any>, model?: string) => {
    // Switch to preview tab when query starts running
    if (onTabChange) {
      onTabChange('preview');
    }
    try {
      // Send the query to the AI service
      const response = await sendQuery(resolvedPrompt, queryType, AIProviders.OpenAI, model);

      if (response.content) {
        // Store the raw response
        setResponseData(response);

        // If parsing is requested, try to parse the response
        if (parseResponse) {
          try {
            // Create a properly typed response object for the parser
            const aiResponse = {
              content: response.content,
              model: response.model,
              provider: response.provider,
              timestamp: new Date().toISOString(),
              ...response, // Include any other properties
            };

            // Use the AI JSON Parser to handle the response
            const parseResult = parseAIResponse(aiResponse, { debug: true });

            if ('error' in parseResult) {
              // Handle parsing error
              const errorMsg = `Failed to parse AI response: ${parseResult.message}`;
              setError(errorMsg);
              console.error('AI Response Parsing Error:', parseResult);
              console.error('Original LLM Response:', response.content);

              // Call onResponse with the raw response if parsing fails
              if (onResponse) {
                onResponse(response, 'Parse Fails');
              }

              // Return the raw response text
              return response.content;
            } else {
              // Successfully parsed the response
              if (onResponse) {
                onResponse(parseResult, 'Parse Success');
              }

              // Return the formatted response for display
              return JSON.stringify(parseResult, null, 2);
            }
          } catch (parseError) {
            console.error('Error parsing response:', parseError);
            // Return the raw response if parsing fails
            if (onResponse) {
              onResponse(response, 'Parse Fails');
            }
            return response.content;
          }
        } else {
          // No parsing requested, just return the content
          if (onResponse) {
            onResponse(response, 'No parsing requested');
          }
          return response.content;
        }
      } else {
        const errorMsg = 'No content in response';
        setError(errorMsg);
        return errorMsg;
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'An unknown error occurred';
      setError(errorMsg);
      console.error('Error running prompt:', error);
      return `Error: ${errorMsg}`;
    }
  };

  return (
    <AIEditor
      prompt={initialPrompt}
      variableData={variableData}
      onClose={onClose}
      onRun={handleRun}
      additionalTabs={additionalTabs}
      activeTabId={activeTabId}
      onTabChange={onTabChange}
      reportData={reportData}
      onConstrainChange={onConstrainChange}
    />
  );
};

export default AIPromptEditor;
